import APP_PATHS from '../../constants/app-paths';
import { InteractionEventTrigger } from '@blueshift-ui/telemetry';
import { ListGroup } from '@llt-frontend/react-material-list';
import PropTypes from 'prop-types';
import React from 'react';
import { emitter as abTestEmitter } from '@marvelapp/react-ab-test';
import addVtSource from '../../utilities/tracking/add-vt-source';
import getNavList from './get-nav-list';
import useAssignments from '../../hooks/data-access/use-assignments';
import useExperiment from '@marvelapp/react-ab-test/lib/hook';
import { useInteractionTracker } from '@blueshift-ui/telemetry';
import useStudentOnDemandCoursesContent from '../../hooks/data-access/use-student-on-demand-courses-content';
import useStudentSubjects from '../../hooks/data-access/use-student-subjects';
import useSubjectNavigationSection from '../../hooks/side-navigation/use-subject-section';
import { useTranslation } from 'react-i18next';
import useUserContext from '../../hooks/context/use-user-context';

//

const EXPERIMENT_SELF_271 = 'SELF-271';
const VARIANT_SELF_271_TEST = 'SELF-271_test';
const VARIANT_SELF_271_CONTROL = 'SELF-271_control';

//

abTestEmitter.defineVariants(EXPERIMENT_SELF_271, [
  VARIANT_SELF_271_TEST,
  VARIANT_SELF_271_CONTROL,
]);

//

function _applyExperiment(navigationList, variant, translate) {
  if (variant === VARIANT_SELF_271_CONTROL) {
    const assessmentNavIndex = navigationList.findIndex(
      ({ heading }) => heading === translate('assessments')
    );
    const [assessmentNavItem] = navigationList.splice(assessmentNavIndex, 1);

    navigationList.push(assessmentNavItem);
  } else {
    const activityLabNavIndex = navigationList.findIndex(
      ({ heading }) => heading === translate('activityLabs')
    );

    if (activityLabNavIndex > -1) {
      const [clubhouseNavItem] = navigationList.splice(activityLabNavIndex, 1);
      navigationList.push(clubhouseNavItem);
    }
  }

  return navigationList;
}

//

function SideNavigation({ onItemClick }) {
  const labUser = useUserContext();
  const { activeStudentId, activeUser } = labUser;

  const isVt4sUser = activeUser?.productState === 'vt4s';

  const { data: assignments } = useAssignments({
    studentId: activeStudentId,
  });

  const activityLabsSubjects = useStudentSubjects({
    studentId: activeStudentId,
  });

  const { data: studentOnDemandCourses, loading: studentOnDemandCoursesLoading } =
    useStudentOnDemandCoursesContent({ gradeAppropriateOnly: true });

  const { activeVariant: activeExperimentVariant } = useExperiment(EXPERIMENT_SELF_271);

  const { trackEvent } = useInteractionTracker();

  const clubNavSection = useSubjectNavigationSection({
    activeStudentId,
    addSubjectLink: APP_PATHS.addActivityLab,
  });

  const selfStudyNavSection = useSubjectNavigationSection({
    activeStudentId,
    addSubjectLink: '/subjects/add',
  });

  const { t: translate, ready: translationLoaded } = useTranslation('side-navigation/index');

  // Memoized

  const navList = React.useMemo(() => {
    if (!translationLoaded) {
      return null;
    }

    return getNavList({
      activityLabsSubjects,
      assignments,
      clubNavSection,
      labUser,
      onDemand: {
        courses: studentOnDemandCourses,
        coursesLoading: studentOnDemandCoursesLoading,
      },
      selfStudyNavSection,
      trackEvent,
      translate,
    });
  }, [
    activityLabsSubjects,
    assignments,
    clubNavSection,
    labUser,
    selfStudyNavSection,
    studentOnDemandCourses,
    studentOnDemandCoursesLoading,
    trackEvent,
    translate,
    translationLoaded,
  ]);

  const deriveAnalytics = React.useCallback(function _deriveAnalytics({ currentTarget }) {
    let label;
    if (currentTarget) {
      label = currentTarget.href;
    }

    return {
      action: 'view-page',
      category: 'side-nav',
      label,
    };
  }, []);

  //
  // Effects

  React.useEffect(() => {
    trackEvent({
      action: EXPERIMENT_SELF_271,
      category: 'experiments',
      label: activeExperimentVariant,
    });
  }, [activeExperimentVariant, trackEvent]);

  //
  // Render

  if (!translationLoaded) {
    return null;
  }

  return (
    <div>
      <InteractionEventTrigger
        analytics={deriveAnalytics}
        element={ListGroup}
        elementProps={{
          onItemClick,
          lists: _applyExperiment(navList, activeExperimentVariant, translate),
        }}
        trigger="onItemClick"
      />

      {!isVt4sUser && (
        <InteractionEventTrigger
          analytics={{
            action: 'nav-home',
            category: 'side-nav',
          }}
          element="a"
          elementProps={{
            className: 'side-navigation__home-link',
            href: addVtSource('/'),
          }}
        >
          {translate('goHome')}
        </InteractionEventTrigger>
      )}
    </div>
  );
}

SideNavigation.propTypes = {
  onItemClick: PropTypes.func,
};

// Exports
// -------

export default SideNavigation;
